/**
 * ImageSelector — unified image picker with 3 modes:
 *  1. Subir archivo (POST /api/admin/media/upload)
 *  2. Pegar URL externa (saved into library so it appears next time)
 *  3. Elegir de la biblioteca multimedia (GET /api/admin/media)
 *
 * Value is always a string URL stored in the parent field.
 */
import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Link2, Library, X, Trash2, Loader2 } from "lucide-react";
import { mediaApi, formatApiError, API } from "./api";

const MAX_BYTES = 8 * 1024 * 1024; // 8MB
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

function absoluteUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  // /api/admin/media/.../raw -> prepend backend
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function ImageSelector({ value, onChange, label = "Imagen" }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">
        {label}
      </span>
      <div className="flex items-stretch gap-3">
        <div
          className="w-24 h-20 flex-shrink-0 border border-[#16335A]/15 bg-[#FDFBF7] flex items-center justify-center overflow-hidden"
          style={{ borderRadius: "2px" }}
        >
          {value ? (
            <img src={absoluteUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-[#16335A]/30" />
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <input
            type="url"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://... o usa el selector →"
            data-testid="image-selector-url"
            className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="image-selector-open"
              className="bg-[#16335A] hover:bg-[#214A82] text-white px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
            >
              <Library size={12} /> Seleccionar
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid="image-selector-clear"
                className="text-[#3A506B] hover:text-red-700 text-xs inline-flex items-center gap-1"
              >
                <X size={12} /> Quitar
              </button>
            )}
          </div>
        </div>
      </div>

      {open && (
        <ImageSelectorModal
          currentValue={value}
          onClose={() => setOpen(false)}
          onPick={(url) => {
            onChange(url);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function ImageSelectorModal({ currentValue, onPick, onClose }) {
  const [tab, setTab] = useState("library"); // library | upload | url
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const data = await mediaApi.list();
      setItems(data);
    } catch (err) {
      setError(formatApiError(err));
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setError("Formato no permitido. Usa JPG, PNG, WebP o GIF.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("La imagen supera los 8 MB.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const m = await mediaApi.upload(file);
      const url = m.url || m.external_url || "";
      setItems((prev) => [m, ...(prev || [])]);
      onPick(absoluteUrl(url));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleExternal = async () => {
    const url = externalUrl.trim();
    if (!url.startsWith("http")) {
      setError("La URL debe empezar por http o https.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const m = await mediaApi.addExternal(url);
      setItems((prev) => [m, ...(prev || [])]);
      onPick(m.url || url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta imagen de la biblioteca?")) return;
    try {
      await mediaApi.remove(id);
      setItems((prev) => (prev || []).filter((it) => it.id !== id));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-4xl w-full max-h-[88vh] flex flex-col"
        style={{ borderRadius: "2px" }}
        data-testid="image-selector-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#16335A]/10">
          <h3 className="font-display text-2xl text-[#0A192F]">Biblioteca multimedia</h3>
          <button onClick={onClose} className="text-[#3A506B] hover:text-[#16335A]" data-testid="image-selector-close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-[#16335A]/10 flex gap-1">
          {[
            ["library", "Biblioteca", Library],
            ["upload", "Subir archivo", Upload],
            ["url", "URL externa", Link2],
          ].map(([k, l, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setTab(k); setError(null); }}
              data-testid={`image-tab-${k}`}
              className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${
                tab === k
                  ? "border-[#16335A] text-[#16335A]"
                  : "border-transparent text-[#3A506B] hover:text-[#16335A]"
              }`}
            >
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <p className="mb-4 text-red-700 bg-red-50 border border-red-200 px-4 py-2 text-sm" data-testid="image-selector-error">
              {error}
            </p>
          )}

          {tab === "library" && (
            <>
              {items === null && <p className="text-[#3A506B] italic">Cargando...</p>}
              {items && items.length === 0 && (
                <div className="bg-[#FDFBF7] border border-dashed border-[#16335A]/20 p-10 text-center" style={{ borderRadius: "2px" }}>
                  <ImageIcon size={32} className="mx-auto text-[#16335A]/30" />
                  <p className="mt-3 text-sm text-[#3A506B]">La biblioteca está vacía. Sube tu primera imagen.</p>
                </div>
              )}
              {items && items.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {items.map((it) => {
                    const url = absoluteUrl(it.url || "");
                    const selected = url && (url === currentValue || it.url === currentValue);
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => onPick(url)}
                        data-testid={`image-item-${it.id}`}
                        className={`group relative aspect-square bg-[#FDFBF7] overflow-hidden border-2 transition-all ${
                          selected ? "border-[#E8C97A] ring-2 ring-[#E8C97A]/40" : "border-[#16335A]/10 hover:border-[#16335A]/40"
                        }`}
                        style={{ borderRadius: "2px" }}
                      >
                        {url ? (
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex items-center justify-center h-full text-[#16335A]/30">
                            <ImageIcon size={28} />
                          </div>
                        )}
                        <span
                          onClick={(e) => handleDelete(it.id, e)}
                          className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-700 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          style={{ borderRadius: "2px" }}
                          title="Eliminar"
                        >
                          <Trash2 size={11} />
                        </span>
                        {it.source === "external" && (
                          <span className="absolute bottom-1.5 left-1.5 text-[10px] bg-black/60 text-white px-1.5 py-0.5 uppercase tracking-wider" style={{ borderRadius: "2px" }}>
                            URL
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {tab === "upload" && (
            <div className="space-y-4">
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleFile(f);
                }}
                className="bg-[#FDFBF7] border-2 border-dashed border-[#16335A]/25 hover:border-[#16335A]/60 cursor-pointer p-10 text-center transition-colors"
                style={{ borderRadius: "2px" }}
                data-testid="image-dropzone"
              >
                {uploading ? (
                  <>
                    <Loader2 size={32} className="mx-auto text-[#16335A] animate-spin" />
                    <p className="mt-3 text-sm text-[#3A506B]">Subiendo y optimizando...</p>
                  </>
                ) : (
                  <>
                    <Upload size={32} className="mx-auto text-[#16335A]/50" />
                    <p className="mt-3 font-display text-lg text-[#0A192F]">Arrastra una imagen aquí</p>
                    <p className="mt-1 text-xs text-[#3A506B]">o haz click para seleccionar · JPG, PNG, WebP, GIF · máx 8 MB</p>
                  </>
                )}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept={ALLOWED.join(",")}
                className="hidden"
                data-testid="image-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {tab === "url" && (
            <div className="space-y-4">
              <label className="block">
                <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">URL de la imagen</span>
                <input
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://images.example.com/foto.jpg"
                  data-testid="image-external-input"
                  className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
                  style={{ borderRadius: "2px" }}
                />
              </label>
              <button
                type="button"
                onClick={handleExternal}
                disabled={busy || !externalUrl.trim()}
                data-testid="image-external-submit"
                className="bg-[#16335A] hover:bg-[#214A82] disabled:opacity-50 text-white px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                Usar esta URL
              </button>
              <p className="text-xs text-[#3A506B]">
                Guardamos la URL en la biblioteca para que puedas reutilizarla.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
